/** tm/legal/LegalSettingsModal.tsx — 주제·알림 설정 (슈퍼관리자): 주제 · 감시 법령 · 알림 탭. 모바일은 전체 화면.
 *  저장하지 않은 변경이 있으면 탭 이동·닫기(×·Esc·바깥·뒤로 가기) 전에 인라인 막대 「저장 / 버리기 / 계속 편집」. */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { C, T, R } from '../components/ui';
import { btn, MUTED } from './legalTheme';
import { useBackLayer } from './useBackLayer';
import type { SettingsGuard } from './settingsGuard';
import type { ShowToast } from './legalUi';
import LegalTopicsTab from './LegalTopicsTab';
import LegalLawsTab from './LegalLawsTab';
import LegalAlertSettingsTab from './LegalAlertSettingsTab';

type Tab = 'topics' | 'laws' | 'alerts';
const TABS: [Tab, string][] = [['topics', '주제'], ['laws', '감시 법령'], ['alerts', '알림']];

interface Props { mobile: boolean; onClose: () => void; onToast: ShowToast }

export default function LegalSettingsModal({ mobile, onClose, onToast }: Props) {
  const [tab, setTab] = useState<Tab>('topics');
  const [pending, setPending] = useState<(() => void) | null>(null);
  const [saving, setSaving] = useState(false);
  const dirty = useRef(false);
  const saveFn = useRef<(() => Promise<boolean>) | null>(null);
  const box = useRef<HTMLDivElement>(null);

  const guard = useMemo<SettingsGuard>(() => ({
    report: (d, s) => { dirty.current = d; saveFn.current = s; if (!d) setPending(null); },
    request: action => { if (dirty.current) setPending(() => action); else action(); },
  }), []);
  const tryClose = useCallback(() => guard.request(onClose), [guard, onClose]);
  useBackLayer(true, tryClose);
  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') tryClose(); };
    document.addEventListener('keydown', k);
    box.current?.focus();
    return () => document.removeEventListener('keydown', k);
  }, [tryClose]);

  const go = (action: () => void) => { dirty.current = false; setPending(null); action(); };
  const saveThen = async () => {
    const act = pending;
    if (!act) return;
    setSaving(true);
    const ok = saveFn.current ? await saveFn.current() : true;
    setSaving(false);
    if (ok) go(act);
  };

  return (
    <div onMouseDown={e => { if (e.target === e.currentTarget) tryClose(); }} style={{
      position: 'fixed', inset: 0, background: 'rgba(17,24,39,.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: mobile ? 0 : 24,
    }}>
      <div ref={box} role="dialog" aria-modal="true" aria-label="주제·알림 설정" tabIndex={-1} style={{
        background: C.white, width: mobile ? '100%' : 'min(980px, 100%)', height: mobile ? '100%' : 'min(820px, 100%)', borderRadius: mobile ? 0 : R.lg,
        display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 24px 48px rgba(17,24,39,.25)', outline: 'none',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', padding: mobile ? '8px 8px 0 14px' : '12px 12px 0 20px', gap: 8 }}>
          <h2 style={{ margin: 0, fontSize: T.lg, fontWeight: 800, color: C.text, flex: 1 }}>주제·알림 설정</h2>
          <button type="button" className="lw-f" onClick={tryClose} aria-label="닫기" style={{ border: 0, background: 'transparent', fontSize: 22, color: MUTED, cursor: 'pointer', width: 44, height: 44, lineHeight: 1, borderRadius: R.sm }}>×</button>
        </div>
        <div role="tablist" style={{ display: 'flex', gap: 2, padding: mobile ? '2px 10px 0' : '4px 16px 0', borderBottom: `1px solid ${C.border}` }}>
          {TABS.map(([k, l]) => (
            <button key={k} type="button" role="tab" aria-selected={tab === k} className="lw-f" onClick={() => { if (k !== tab) guard.request(() => setTab(k)); }} style={{
              border: 0, background: 'transparent', padding: '10px 14px', minHeight: 44, fontSize: T.md, fontWeight: tab === k ? 700 : 600, cursor: 'pointer',
              color: tab === k ? C.accent : C.sub, borderBottom: `2px solid ${tab === k ? C.accent : 'transparent'}`, fontFamily: 'inherit',
            }}>{l}</button>
          ))}
        </div>
        {pending && (
          <div role="alert" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '8px 14px', background: '#FFFBEB', borderBottom: '1px solid #FDE68A', fontSize: T.md, color: '#92400E' }}>
            <b style={{ flex: '1 1 200px' }}>저장하지 않은 변경이 있습니다</b>
            <button type="button" className="lw-f" disabled={saving} onClick={saveThen} style={{ ...btn('pri'), opacity: saving ? 0.6 : 1 }}>{saving ? '저장 중…' : '저장'}</button>
            <button type="button" className="lw-f" onClick={() => pending && go(pending)} style={btn()}>버리기</button>
            <button type="button" className="lw-f" onClick={() => setPending(null)} style={{ ...btn(), border: 0, background: 'transparent' }}>계속 편집</button>
          </div>
        )}
        <div style={{ flex: 1, overflowY: 'auto', padding: mobile ? '14px 14px 0' : '20px 20px 0', position: 'relative' }}>
          {tab === 'topics' && <LegalTopicsTab mobile={mobile} onToast={onToast} guard={guard} />}
          {tab === 'laws' && <LegalLawsTab onToast={onToast} guard={guard} />}
          {tab === 'alerts' && <LegalAlertSettingsTab onToast={onToast} guard={guard} />}
        </div>
      </div>
    </div>
  );
}
